// src/lib/api/presentations/presentation-execution-history-api-call.ts
import store from '../../../store/store';
import { asyncHandler } from '../async-handler';
import { RequestInfo } from '../../../interfaces/RequestInfo';
import { PRESENTATIONS_API } from '../../constants/central-api';

/* ------------------------- Interfaces (typed) ------------------------- */

export type PresentationExecutionStatus =
  | 'queued'
  | 'processing'
  | 'completed'
  | 'failed';

export interface PresentationExecutionHistoryItem {
  id: string;
  presentation_name: string;
  status: PresentationExecutionStatus;
  executed_at: string; // ISO datetime
  duration_seconds: number | null;
  error_message: string | null;
  presentation_url: string | null;
  batch_identifier: string;
  processing_date: string; // YYYY-MM-DD
}

export interface PresentationExecutionHistoryPagination {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  from: number | null;
  to: number | null;
  has_more_pages: boolean;
}

export interface PresentationExecutionHistoryApiEnvelope {
  success: boolean;
  message: string;
  data: {
    data: PresentationExecutionHistoryItem[];
    pagination: PresentationExecutionHistoryPagination;
  };
}

export interface PresentationExecutionHistoryResult {
  items: PresentationExecutionHistoryItem[];
  pagination: PresentationExecutionHistoryPagination;
}

/* ----------------------------- URL builder ---------------------------- */
/** Example: /api/v2/shops/{slug}/presentations/execution-history?per_page=5&page=1 */
const PRESENTATIONS_EXECUTION_HISTORY_API = (
  shopSlug: string,
  page: number,
  perPage: number
) =>
  `${PRESENTATIONS_API(
    shopSlug
  )}/execution-history?per_page=${perPage}&page=${page}`;

/* ------------------------------- API call ----------------------------- */

export default async function get_presentation_execution_history_api_call(
  page: number = 1,
  perPage: number = 5
): Promise<PresentationExecutionHistoryResult | null> {
  try {
    const shopSlug = store.getState().user?.company?.shop_slug;
    const accessToken = store.getState().auth.authorization.accessToken;
    const sellerToken = accessToken?.sellerToken;

    const requestInfo: RequestInfo = {
      url: PRESENTATIONS_EXECUTION_HISTORY_API(shopSlug, page, perPage),
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: accessToken.type + ' ' + sellerToken,
      },
    };

    const actionType = 'GET_PRESENTATION_EXECUTION_HISTORY';
    const customMsg = 'Presentation execution history retrieved successfully';

    const res: any = await asyncHandler(
      requestInfo,
      actionType,
      customMsg,
      false
    );
    const response: PresentationExecutionHistoryApiEnvelope = res?.data;

    if (res?.actionType === actionType && response?.success === true) {
      return {
        items: response.data.data || [],
        pagination: response.data.pagination,
      };
    } else {
      return null;
    }
  } catch (exception) {
    console.log('Error during get presentation execution history request');
    return null;
  }
}
